import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { Object_Template } from "graphql/generated/graphql";
import { getRelativeUrl } from "utils/service";
import styles from "./ObjectTemplateCard.module.scss";
import { formatObjectCount } from "./relationGraphFetch";
import { READ_ONLY_MAX_VISIBLE_ATTRS } from "./utils";

export interface ObjectTemplateReadOnlyCardProps {
    data: Object_Template;
    /**
     * When provided (interactive template canvas), render an edit affordance that
     * expands this compact card into its full editable attributes-form. Omitted in
     * read-only embed (dashboard) mode, which stays purely read-only.
     */
    onExpand?: () => void;
    /**
     * id → name map of the templates currently in the graph, used to resolve an
     * OBJECT_TEMPLATE_REFERENCE attribute's target to a human-readable name. Absent
     * entries fall back to a generic "reference" label (the target may be outside
     * the loaded batch).
     */
    referenceTargetNames?: { [id: string]: string };
}

/**
 * Human-readable type suffix for one attribute row (shown muted after the name):
 * reference attributes point at their target ("→ <Template>" / "→ <RECORD_TYPE>"),
 * every other datatype shows its humanised name. Kept as plain inert text — an
 * overview attribute is display-only, never an interactive control
 * (ui-design-principle-affordance-interactivity).
 */
const getAttributeTypeLabel = (
    attribute: {
        datatype?: string | null;
        reference_object_template_id?: string | null;
        reference_record_type?: string | null;
    },
    referenceTargetNames?: { [id: string]: string }
): string => {
    const datatype = attribute?.datatype ?? "";
    if (datatype === "OBJECT_TEMPLATE_REFERENCE") {
        const targetName =
            (attribute.reference_object_template_id &&
                referenceTargetNames?.[attribute.reference_object_template_id]) ||
            null;
        return targetName ? `→ ${targetName}` : "→ reference";
    }
    if (datatype === "RECORD_REFERENCE") {
        return attribute.reference_record_type
            ? `→ ${attribute.reference_record_type}`
            : "→ record";
    }
    return datatype.toLowerCase().replace(/_/g, " ");
};

/**
 * Compact read-only card for object templates. Used both in embedded relation graphs
 * (dashboard widgets) and as the default lightweight overview card on the interactive
 * object-template canvas, where an edit affordance expands it into the editable card.
 *
 * It shows the full template at a glance — icon + name (link), the live custom-object
 * count, and the template's attribute list (name · type, references resolved to their
 * target) — WITHOUT mounting the heavy editable attributes-form (≈1 form/template, the
 * several-seconds load + fan-whirr on many-template canvases:
 * operator-task-fe-canvas-loading-perf-followup, item C1). All of this data already
 * arrives with the overview fetch (icon + custom_object_count + the first
 * ATTRIBUTES_PER_BATCH attributes), so the richer card costs no extra network.
 */
export const ObjectTemplateReadOnlyCard = ({
    data,
    onExpand,
    referenceTargetNames,
}: ObjectTemplateReadOnlyCardProps) => {
    // The object-template canvas loads the counts in a second pass after the
    // nodes paint (operator-task-fe-object-canvas-slow-graphql-pages), so the
    // count is undefined until then and the line shows a placeholder.
    const objectCount: number | undefined =
        data?.custom_object_list_object_template_id?.custom_object_count ?? undefined;
    const attributeList =
        data?.object_template_attribute_list_object_template_id?.object_template_attribute_list ??
        [];
    const attributeCount =
        data?.object_template_attribute_list_object_template_id?.object_template_attribute_count ??
        attributeList.length;
    const shownAttributes = attributeList.slice(0, READ_ONLY_MAX_VISIBLE_ATTRS);
    // Attributes beyond what is shown — either loaded-but-over-the-cap, or not yet
    // fetched (the overview fetches only the first page). Surfaced as "+N more" so the
    // count is never silently truncated; the full list is reachable via the edit form.
    const hiddenAttributeCount = Math.max(attributeCount - shownAttributes.length, 0);

    return (
        <div className={`${styles.formWrapper} ${styles.readOnlyCard}`}>
            <div className={styles.readOnlyHeader}>
                <RecordLabel
                    image={data?.icon}
                    iconByType="object_template"
                    name={data?.name}
                    link={data?.id ? getRelativeUrl("object_template", data.id) : null}
                    // Standard (non-overlapping) truncation tooltip on the canvas —
                    // the in-place "overlap" tooltip is portalled outside the
                    // react-flow zoom transform and can never sit centered/at-size
                    // over the scaled title. Edge-aware so the caret keeps pointing
                    // at nodes near the canvas border
                    // (operator-task-fe-canvas-title-hover-centering-font-size).
                    hasOverlappingTooltip={false}
                    edgeAwarePlacement
                />
                {onExpand && (
                    <Button
                        variation="icon"
                        icon={IconCollection.pencilOutline}
                        ariaLabel="Edit template"
                        title="Edit template"
                        labelOnlyAria
                        className={styles.readOnlyEditButton}
                        onClick={(event) => {
                            event.stopPropagation();
                            onExpand();
                        }}
                    />
                )}
            </div>

            {data?.id && (
                <div className={styles.objectCount}>
                    {formatObjectCount(objectCount)}
                </div>
            )}

            {shownAttributes.length > 0 && (
                <div className={styles.readOnlyAttributes}>
                    <div className={styles.readOnlyAttributesTitle}>Attributes</div>
                    {shownAttributes.map((attribute) => (
                        <div className={styles.readOnlyAttributeRow} key={attribute?.id}>
                            <span className={styles.readOnlyAttributeName} title={attribute?.name}>
                                {attribute?.name}
                            </span>
                            <span className={styles.readOnlyAttributeType}>
                                {getAttributeTypeLabel(attribute ?? {}, referenceTargetNames)}
                            </span>
                        </div>
                    ))}
                    {hiddenAttributeCount > 0 && (
                        <div className={styles.readOnlyAttributeMore}>
                            +{hiddenAttributeCount} more
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
